import React, { useContext } from 'react'
import styled, { css, ThemeProvider } from 'styled-components'
import { AntdConfigImpl } from 'styles/AntdConfig'
import CantileverTheme from 'styles/cantileverTheme'
import { GlobalStyles } from 'styles/GlobalStyles'

import {
  ForecastBarbieTheme,
  ForecastBratRemixTheme,
  ForecastBratTheme,
  ForecastDarkTheme,
  ForecastFreedomTheme,
  ForecastHotDogStandTheme,
  ForecastLightTheme,
  ForecastSpookyTheme,
  ForecastZincTheme,
} from './themes'

export enum ThemeMode {
  Dark = 'Dark',
  Light = 'Light',
  Zinc = 'Zinc',
  Freedom = 'Freedom',
  Spooky = 'Spooky',
  Barbie = 'Barbie',
  Brat = 'Brat',
  BratRemix = 'BratRemix',
  HotDog = 'HotDog',
}

const ThemeMap = {
  [ThemeMode.Dark]: ForecastDarkTheme,
  [ThemeMode.Light]: ForecastLightTheme,
  [ThemeMode.Zinc]: ForecastZincTheme,
  [ThemeMode.Freedom]: ForecastFreedomTheme,
  [ThemeMode.Spooky]: ForecastSpookyTheme,
  [ThemeMode.Barbie]: ForecastBarbieTheme,
  [ThemeMode.Brat]: ForecastBratTheme,
  [ThemeMode.BratRemix]: ForecastBratRemixTheme,
  [ThemeMode.HotDog]: ForecastHotDogStandTheme,
}

const CommonColors = {
  alert: {
    notice: 'rgb(210, 210, 210)',
    warning: 'rgb(255, 225, 0)',
    error: 'rgb(245, 50, 30)',
  },
  badge: {
    background: {
      blue: '#4A90E266',
      green: '#50C87866',
      orange: '#FFB34766',
      yellow: '#FFD70066',
      purple: '#80008066',
      darkGreen: '#00640066',
      gray: '#D8D8D866',
    },
    text: {
      blue: '#D8E3F2',
      green: '#D8E9DE',
      orange: '#F2E3D8',
      yellow: '#F2F2D8',
      purple: '#D8D8F2',
      darkGreen: '#D8E9DE',
      gray: '#D8D8D8',
    },
  },
}

const ThemeObj = {
  forecast: ForecastDarkTheme,
  common: CommonColors,
  ...CantileverTheme,
}

export type ThemeType = typeof ThemeObj

export const GeckoTheme: ThemeType = {
  ...ThemeObj,
} as const

const DefaultStyles = styled.div<{ $skipDefaultStyles: boolean }>`
  ${({ $skipDefaultStyles, theme }) =>
    $skipDefaultStyles
      ? css`
          display: contents;
        `
      : css`
          background-color: ${theme.forecast.background1};
          color: ${theme.forecast.activeText};
          fill: ${theme.forecast.icon1};
          height: 100%;
        `}
`

const THEME_MODE_SETTING_ID = 'gecko_theme_mode'

export const GeckoThemeProvider: React.FC<{
  children: React.ReactNode
  skipDefaultStyles?: boolean
}> = ({ children, skipDefaultStyles = false }) => {
  const [themeMode, internalSetSystem] = React.useState<ThemeMode>(
    () =>
      ThemeMode[
        (localStorage.getItem(
          THEME_MODE_SETTING_ID
        ) as keyof typeof ThemeMode) ?? ThemeMode.Dark
      ]
  )

  const setThemeMode = React.useCallback((themeMode: ThemeMode) => {
    localStorage.setItem(THEME_MODE_SETTING_ID, themeMode)
    internalSetSystem(themeMode)
  }, [])
  const [themeModeOverride, setThemeModeOverride] = React.useState<
    ThemeMode | undefined
  >(undefined)
  const effectiveThemeMode = themeModeOverride ?? themeMode

  const [theme, setTheme] = React.useState(ThemeObj)

  const { dynamicTheme } = React.useMemo(() => {
    const newThemeObj = {
      dynamicTheme: {
        ...ThemeObj,
        forecast: ThemeMap[effectiveThemeMode] ?? ForecastDarkTheme,
      },
    }
    setTheme(newThemeObj.dynamicTheme)
    return newThemeObj
  }, [effectiveThemeMode])

  return (
    <GeckoThemeContext.Provider
      value={{
        themeMode: effectiveThemeMode,
        themeModeOverride,
        setThemeMode,
        setThemeModeOverride,
        theme,
        setTheme,
      }}>
      <ThemeProvider theme={dynamicTheme}>
        <AntdConfigImpl>
          <DefaultStyles $skipDefaultStyles={skipDefaultStyles}>
            {!skipDefaultStyles && <GlobalStyles />}
            {children}
          </DefaultStyles>
        </AntdConfigImpl>
      </ThemeProvider>
    </GeckoThemeContext.Provider>
  )
}

export type GeckoThemeContextType = {
  themeMode: ThemeMode
  themeModeOverride: ThemeMode | undefined
  setThemeMode: (themeMode: ThemeMode) => void
  setThemeModeOverride: (themeMode: ThemeMode | undefined) => void
  theme: ThemeType
  setTheme: React.Dispatch<React.SetStateAction<ThemeType>>
}

const GeckoThemeContext = React.createContext<
  GeckoThemeContextType | undefined
>(undefined)

export const useGeckoTheme = () => {
  const context = useContext(GeckoThemeContext)
  if (!context)
    throw new Error('useGeckoTheme() must be used within GeckoThemeProvider')
  return context
}

export function useGeckoThemeModeOverride(themeMode: ThemeMode | undefined) {
  const { setThemeModeOverride } = useGeckoTheme()

  React.useLayoutEffect(() => {
    setThemeModeOverride(themeMode)

    return () => setThemeModeOverride(undefined)
  }, [setThemeModeOverride, themeMode])
}
